/**
 * Permission Context for global permission state management
 */
import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const PermissionContext = createContext(null);

/**
 * Permission Provider Component
 */
export function PermissionProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.permissions) {
      // Convert permissions array to object for faster lookup
      const permMap = {};
      user.permissions.forEach((perm) => {
        permMap[perm] = true;
      });
      setPermissions(permMap);
      setLoading(false);
    } else {
      setPermissions({});
      setLoading(false);
    }
  }, [user]);

  /**
   * Check if user has a specific permission
   * @param {string} module - Module name
   * @param {string} action - Action name
   * @returns {boolean} Whether user has permission
   */
  const hasPermission = (module, action) => {
    const key = `${module}:${action}`;
    return permissions[key] === true;
  };

  /**
   * Check if user has any of the specified permissions
   * @param {Array<{module: string, action: string}>} perms - Array of permissions
   * @returns {boolean} Whether user has any of the permissions
   */
  const hasAnyPermission = (perms) => {
    return perms.some((perm) => hasPermission(perm.module, perm.action));
  };

  /**
   * Check if user has all of the specified permissions
   * @param {Array<{module: string, action: string}>} perms - Array of permissions
   * @returns {boolean} Whether user has all of the permissions
   */
  const hasAllPermissions = (perms) => {
    return perms.every((perm) => hasPermission(perm.module, perm.action));
  };

  const value = {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Hook to use permission context
 */
export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}
